import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { getActivityPeriodSummary } from '../../../../services/activity';

const PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

const getPeriodStart = (period, plans) => {
    if (PERIOD_DAYS[period]) {
        return dayjs()
            .subtract(PERIOD_DAYS[period] - 1, 'day')
            .startOf('day');
    }

    const today = dayjs();
    const earliestPlanStart = plans
        .filter(
            (plan) =>
                plan.accepted &&
                dayjs(plan.start_date).isValid() &&
                !dayjs(plan.start_date).isAfter(today, 'day')
        )
        .map((plan) => dayjs(plan.start_date).startOf('day'))
        .sort((first, second) => first.valueOf() - second.valueOf())[0];

    return earliestPlanStart || dayjs().subtract(1, 'year').startOf('day');
};

const aggregateByMonth = (periods) => {
    const months = new Map();

    periods.forEach((period) => {
        const monthKey = dayjs(period.startDate).format('YYYY-MM');
        const current = months.get(monthKey) || {
            startDate: dayjs(period.startDate).startOf('month').format('YYYY-MM-DD'),
            completed: 0,
            missed: 0
        };
        current.completed += period.completed;
        current.missed += period.missed;
        months.set(monthKey, current);
    });

    return Array.from(months.values()).map((month) => {
        const total = month.completed + month.missed;
        return {
            ...month,
            total,
            adherence: total > 0 ? Math.round((month.completed / total) * 100) : null
        };
    });
};

const prepareChartData = (periods, period) => {
    if (period === 'all') return aggregateByMonth(periods);

    return periods.map((item) => ({
        ...item,
        adherence: item.total > 0 ? Math.round((item.completed / item.total) * 100) : null
    }));
};

const ActivityEvolutionChart = ({
    patientUuid,
    plans,
    plansLoading,
    plansError,
    period
}) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const startDate = getPeriodStart(period, plans);
    const startDateKey = startDate.format('YYYY-MM-DD');
    const todayKey = dayjs().format('YYYY-MM-DD');
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: period === 'all' ? undefined : 'numeric',
                month: 'short',
                year: period === 'all' ? '2-digit' : undefined
            }),
        [locale, period]
    );

    const activityQuery = useQuery({
        queryKey: [
            'patient-activity-evolution',
            patientUuid,
            period,
            startDateKey,
            todayKey
        ],
        queryFn: () =>
            getActivityPeriodSummary({
                queryClient,
                patientUuid,
                startDate: startDateKey,
                endDate: dayjs(),
                groupPartialByDay: period === '7d'
            }),
        enabled: Boolean(patientUuid) && !plansLoading && !plansError,
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const chartData = useMemo(
        () => prepareChartData(activityQuery.data?.periods || [], period),
        [activityQuery.data?.periods, period]
    );
    const hasActivity = chartData.some((item) => item.total > 0);
    const isLoading = plansLoading || (!plansError && activityQuery.isPending);
    const isError = plansError || activityQuery.isError;

    if (isLoading) {
        return (
            <Box sx={{ display: 'grid', gap: 1, height: 300, pt: 1 }}>
                <Skeleton width="25%" height={20} />
                <Skeleton variant="rounded" height={260} />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: 300,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5
                }}
            >
                <Typography color="error" variant="body2">
                    {t('userPage.overview.rehabilitation.activityChart.loadError')}
                </Typography>
                {!plansError && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => activityQuery.refetch()}
                    >
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                )}
            </Box>
        );
    }

    if (!hasActivity) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: 300,
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t('userPage.overview.rehabilitation.activityChart.noData')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            role="img"
            aria-label={t('userPage.overview.rehabilitation.activityChart.chartLabel')}
            sx={{ width: '100%', height: 300 }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 0.5
                }}
            >
                {t('userPage.overview.rehabilitation.activityChart.allPlans')}
            </Typography>
            <ResponsiveContainer width="100%" height={278}>
                <ComposedChart
                    data={chartData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke="#E6EDF2"
                        strokeDasharray="3 3"
                    />
                    <XAxis
                        dataKey="startDate"
                        axisLine={{ stroke: '#CAD5DD' }}
                        tickLine={false}
                        minTickGap={24}
                        tick={{ fill: '#74838E', fontSize: 10 }}
                        tickFormatter={(value) =>
                            dateFormatter.format(dayjs(value).toDate())
                        }
                    />
                    <YAxis
                        yAxisId="exercises"
                        width={38}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#74838E', fontSize: 10 }}
                    />
                    <YAxis
                        yAxisId="adherence"
                        orientation="right"
                        width={40}
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fill: '#74838E', fontSize: 10 }}
                    />
                    <Tooltip
                        labelFormatter={(value) =>
                            dateFormatter.format(dayjs(value).toDate())
                        }
                        formatter={(value, name, item) => [
                            item.dataKey === 'adherence' ? `${value}%` : value,
                            name
                        ]}
                        contentStyle={{
                            border: '1px solid #D7E0E6',
                            borderRadius: 6,
                            boxShadow: '0 4px 12px rgba(38, 50, 56, 0.12)',
                            fontSize: 11
                        }}
                    />
                    <Legend
                        verticalAlign="top"
                        align="right"
                        height={24}
                        iconSize={10}
                        wrapperStyle={{ color: '#5E6C76', fontSize: 10 }}
                    />
                    <Bar
                        yAxisId="exercises"
                        dataKey="completed"
                        name={t(
                            'userPage.overview.rehabilitation.activityChart.completed'
                        )}
                        stackId="exercises"
                        fill="#0A6FB4"
                        maxBarSize={34}
                        animationDuration={450}
                    />
                    <Bar
                        yAxisId="exercises"
                        dataKey="missed"
                        name={t('userPage.overview.rehabilitation.activityChart.missed')}
                        stackId="exercises"
                        fill="#A9CFEA"
                        maxBarSize={34}
                        animationDuration={450}
                    />
                    <Line
                        yAxisId="adherence"
                        type="monotone"
                        dataKey="adherence"
                        name={t(
                            'userPage.overview.rehabilitation.activityChart.adherence'
                        )}
                        stroke="#168A0D"
                        strokeWidth={2}
                        dot={{ r: 2.5, fill: '#168A0D', strokeWidth: 0 }}
                        activeDot={{ r: 4, strokeWidth: 0 }}
                        connectNulls={false}
                        animationDuration={450}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

ActivityEvolutionChart.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    plans: PropTypes.arrayOf(PropTypes.object).isRequired,
    plansLoading: PropTypes.bool.isRequired,
    plansError: PropTypes.bool.isRequired,
    period: PropTypes.oneOf(['7d', '30d', '90d', 'all']).isRequired
};

export default ActivityEvolutionChart;
